import { z } from 'zod';
import { id, paginatedResult, paginationQuery } from './common.js';

export const savedShiftState = z.object({
  shiftId: id,
  saved: z.boolean(),
  revision: z.number().int().nonnegative(),
  canSave: z.boolean(),
  unavailableReason: z.string().nullable(),
});
export type SavedShiftState = z.infer<typeof savedShiftState>;
export const savedShiftStates = z.object({
  canWrite: z.boolean(),
  items: z.array(savedShiftState),
});
export type SavedShiftStates = z.infer<typeof savedShiftStates>;
export const savedShiftInput = z
  .object({
    saved: z.boolean(),
    expectedRevision: z.number().int().nonnegative(),
    clientRequestId: z.string().uuid(),
  })
  .strict();
export type SavedShiftInput = z.infer<typeof savedShiftInput>;
export const savedShiftStateQuery = z
  .object({
    shiftIds: z
      .string()
      .max(18000)
      .transform((value, context): unknown => {
        try {
          return JSON.parse(value) as unknown;
        } catch {
          context.addIssue({ code: 'custom', message: 'Choose a valid list of shifts.' });
          return z.NEVER;
        }
      })
      .pipe(z.array(id.max(150)).min(1).max(100))
      .transform((values) => [...new Set(values)]),
  })
  .strict();
export const savedShiftQuery = paginationQuery
  .extend({
    search: z.string().trim().max(100).optional(),
    availability: z.enum(['all', 'available', 'unavailable']).default('all'),
  })
  .strict();
export type SavedShiftQuery = z.infer<typeof savedShiftQuery>;
export const savedShift = savedShiftState.extend({
  savedAt: z.string(),
  updatedAt: z.string(),
  roleRequired: z.string().nullable(),
  locationName: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  timezone: z.string(),
  startsAt: z.string().nullable(),
  endsAt: z.string().nullable(),
  payRateCents: z.number().int().nonnegative().nullable(),
  status: z.string().nullable(),
  available: z.boolean(),
  canOpen: z.boolean(),
});
export type SavedShift = z.infer<typeof savedShift>;
export const savedShiftPage = paginatedResult(savedShift).extend({ canWrite: z.boolean() });
export type SavedShiftPage = z.infer<typeof savedShiftPage>;
